import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import api from '@helpers/api';
import { useCommunity } from 'contexts/community';

// Content volume as the community payload ships it (`summary`, batched
// server-side); null when the entry predates that field.
function minutesFromSummary(community) {
    const summary = community?.summary;
    if (!summary || typeof summary !== 'object') return null;
    return Number(summary.contents?.minutes) || 0;
}

// Fallback: the same number assembled from the per-community sections.
async function fetchContentMinutes(communityId) {
    const res = await api.get(`community/${communityId}/sections`);
    if (!res || res.isAxiosError) return 0;
    const list = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
    return list.reduce((sum, s) => sum + (Number(s?.summary?.contents?.minutes) || 0), 0);
}

// "12h" above the hour, "45min" below it — like the home tile's well.
function formatContentDuration(t, minutes) {
    const m = Number(minutes) || 0;
    if (m < 60) return t('invite.contentMinutes', { value: m });
    return t('invite.contentHours', { value: Math.round(m / 60) });
}

/**
 * State + handlers for the invitation modal: resolves the owner and the three
 * counters (members · content duration · groups) off the community payload,
 * and runs the join flow through the community context (so the sidebar list
 * refreshes with the new membership).
 */
export default function useInviteModal({ community, onClose, onJoined }) {
    const { t } = useTranslation('community');
    const { community: communityActions, loadCommunities } = useCommunity();

    const [joining, setJoining] = useState(false);

    // Content minutes: straight off `summary`, else fetched once per community.
    const summaryMinutes = minutesFromSummary(community);
    const [fetchedMinutes, setFetchedMinutes] = useState({});

    useEffect(() => {
        const id = community?.id;
        if (!id || summaryMinutes !== null || fetchedMinutes[id] !== undefined) return;

        let cancelled = false;
        fetchContentMinutes(id)
            .catch(() => 0)
            .then(minutes => {
                if (!cancelled) setFetchedMinutes(prev => ({ ...prev, [id]: minutes }));
            });

        return () => { cancelled = true; };
    }, [community?.id, summaryMinutes, fetchedMinutes]);

    const contentMinutes = summaryMinutes ?? fetchedMinutes[community?.id] ?? null;

    const admins = community?.members?.admins || [];
    const owner = useMemo(
        () => admins.find(a => a.role === 'owner') || admins[0] || null,
        [admins]
    );

    const counters = useMemo(() => ([
        { key: 'members', value: String(community?.members?.total ?? 0), loading: false },
        {
            key: 'content',
            value: formatContentDuration(t, contentMinutes),
            loading: contentMinutes === null,
        },
        { key: 'groups', value: String(community?.groups?.length ?? 0), loading: false },
    ]), [community, contentMinutes, t]);

    async function join() {
        if (!community?.id || joining) return;
        setJoining(true);

        try {
            // The api helper resolves with the axios error instead of throwing,
            // in which case `subscribe` hands back no body — treat that as failure.
            const result = await communityActions.subscribe(community.id, () => {});
            if (!result) throw new Error('join failed');

            window.setSnackbar(t('invite.joined', { name: community.name }));
            await loadCommunities();
            onJoined?.();
        } catch (e) {
            console.error(e);
            window.setSnackbar(t('invite.error'));
        }

        setJoining(false);
    }

    function decline() {
        if (joining) return;
        onClose?.();
    }

    return {
        owner,
        counters,
        joining,
        join,
        decline,
        close: decline,
    };
}
